import { post, postStream } from './client.js'

/**
 * Stream chat via POST /api/chat SSE.
 * Calls onEvent({ type, ... }) for each parsed event.
 * Returns { sessionId } when done.
 *
 * The model is not sent — the backend uses whatever default the agent profile
 * specifies for this session.
 *
 * `skillName` and `deactivateSkill` are one-shot directives: `skillName` is
 * set only on the turn where the user picked or switched a skill,
 * `deactivateSkill` only on the turn after the user picked "No skill".
 * Both omitted means "no change". (The model can also exit a skill itself
 * via its own deactivation tool.)
 */
export async function streamChat({ sessionId, message, content, skillName, deactivateSkill, signal, onEvent }) {
  const body = { sessionId, message }
  if (content) body.content = content
  if (skillName) body.skillName = skillName
  if (deactivateSkill) body.deactivateSkill = true
  const res = await postStream('/chat', body, signal)
  const returnedSessionId = res.headers.get('X-Session-Id') || sessionId

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() // keep incomplete line in buffer

    for (const line of lines) {
      const trimmed = line.trim()
      if (!trimmed.startsWith('data:')) continue

      const payload = trimmed.slice(5).trim()
      if (payload === '[DONE]') {
        onEvent({ type: 'done' })
        continue
      }

      try {
        const data = JSON.parse(payload)
        onEvent(data)
      } catch {
        // skip malformed lines
      }
    }
  }

  return { sessionId: returnedSessionId }
}

/**
 * Answer a human-in-the-loop tool-approval request. Plain JSON POST (not a
 * stream) — the backend is holding the original /api/chat SSE stream open and
 * will emit a `tool_approval_resolved` event on it once this decision lands.
 * Returns { resolved: true|false } (false = the decision arrived after the
 * backend's timeout; a harmless no-op).
 */
export async function respondToolApproval({ sessionId, toolCallId, approved }) {
  return post(`/chat/${sessionId}/tool-approval`, { toolCallId, approved })
}

/**
 * Answer a whole batch of human-in-the-loop tool approvals in one call — the
 * bulk sibling of respondToolApproval, used by the grouped approval card. Each
 * decision carries an explicit `approved` per gated tool call; the card always
 * sends one for EVERY still-pending item (unchecked ⇒ `approved: false`) so no
 * gate is left to time out. Plain JSON POST on the side channel, like the
 * single endpoint — the backend confirms each with a `tool_approval_resolved`
 * event on the still-open /api/chat SSE stream. Returns { resolved: <int> }
 * (how many gates the backend actually settled; already-timed-out ones no-op).
 */
export async function respondToolApprovalBatch({ sessionId, decisions }) {
  return post(`/chat/${sessionId}/tool-approval-batch`, { decisions })
}

/**
 * Toggle the session-scoped auto-accept flag. When enabled, the backend skips
 * approval prompts for subsequent turns. Server-side per-session state (not
 * persisted), so it resets when the session changes. Returns { enabled }.
 */
export async function setAutoAccept({ sessionId, enabled }) {
  return post(`/chat/${sessionId}/auto-accept`, { enabled })
}

/**
 * Submit user-provided tool input back to the backend so it can resume the AI.
 * Returns an SSE stream (same as streamChat) since the backend continues the conversation.
 */
export async function streamToolInput({ sessionId, toolCallId, result, signal, onEvent }) {
  const res = await postStream('/chat/tool-input', { sessionId, toolCallId, result }, signal)

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() // keep incomplete line in buffer

    for (const line of lines) {
      const trimmed = line.trim()
      if (!trimmed.startsWith('data:')) continue

      const payload = trimmed.slice(5).trim()
      if (payload === '[DONE]') {
        onEvent({ type: 'done' })
        continue
      }

      try {
        const data = JSON.parse(payload)
        onEvent(data)
      } catch {
        // skip malformed lines
      }
    }
  }
}
